import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FileText, UserCheck, Ban, Trash2, ExternalLink, GitMerge, Link2 } from "lucide-react";
import { ClientDuplicatesPanel, mergeContracts, unifyClient } from "../components/billing/ClientDuplicatesPanel";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

const DISCIPLINE_LABELS = {
  kickboxing: "Kickboxing/Muay Thai", muay_thai: "Kickboxing/Muay Thai", muay_thai_kickboxing: "Kickboxing/Muay Thai",
  jiu_jitsu_can_ryu: "Jiu-jitsu Can Ryu", jiu_jitsu_bresilien: "BJJ",
  jiu_jitsu_kids: "Jiu-Jitsu Kids", jujutsu_japonais: "Jiu-jitsu Can Ryu", power_kicker: "Power Kicker",
  petits_samourais: "Petits Samouraïs",
  mma: "MMA", autre: "Autre",
};

const fmt = (n) => `${Number(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;

function Kpi({ label, value, accent, testid }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

function StatusBadge({ summary, hasSchedule }) {
  if (!hasSchedule) return <span className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400">À configurer</span>;
  const s = summary?.status;
  const map = {
    en_retard: "bg-red-500/20 text-red-300",
    a_jour: "bg-emerald-500/20 text-emerald-300",
  };
  return <span className={`text-[11px] px-2 py-0.5 rounded-full ${map[s] || "bg-zinc-800 text-zinc-400"}`}>{s === "en_retard" ? "En retard" : "À jour"}</span>;
}

export default function ContratsPaiementsModule() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ kpis: {}, contracts: [] });
  const [tariffs, setTariffs] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | overdue | unconfigured | active
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(() => new Set());
  const toggleChecked = (id) => setChecked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const checkedRows = (data.contracts || []).filter((c) => checked.has(c.contract_id)).map((c) => ({ ...c, id: c.contract_id, full_name: c.member_name }));
  const nChecked = checkedRows.length;
  const afterMerge = () => { setChecked(new Set()); load(); };
  const navigate = useNavigate();

  const sendContractPayment = async (c) => {
    if (!c.email) { toast.error("Ajoute d'abord un courriel à ce membre."); return; }
    if (!window.confirm(`Envoyer à ${c.member_name} le contrat d'adhésion (PDF) + le lien de paiement en un seul courriel ?`)) return;
    try {
      const r = await api.post(`/student-contracts/${c.contract_id}/send-contract-and-payment`, { origin_url: window.location.origin });
      toast.success(`Contrat + paiement envoyés à ${r.data.email} ✓`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible."); }
  };
  const confirmActivated = async (c) => {
    if (!window.confirm(`Confirmer manuellement que « ${c.member_name} » est activé ?`)) return;
    try {
      await api.post(`/adhesion/migration/confirm`, { contract_id: c.contract_id });
      toast.success(`${c.member_name} marqué comme activé ✓`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const resilier = async (c) => {
    if (!window.confirm(`Résilier le contrat de ${c.member_name} maintenant ?`)) return;
    try {
      await api.post(`/student-contracts/${c.contract_id}/cancel`, { immediate: true, reason: "Résilié depuis Contrats & Paiements" });
      toast.success(`Contrat de ${c.member_name} résilié.`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Résiliation impossible"); }
  };
  const supprimer = async (c) => {
    if (!window.confirm(`SUPPRIMER définitivement l'élève ${c.member_name} et son contrat ? Action irréversible.`)) return;
    try {
      await api.delete(`/student-contracts/${c.contract_id}`);
      toast.success(`${c.member_name} supprimé.`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  const [bulkBusy, setBulkBusy] = useState(false);
  const bulkSendUnconfigured = async () => {
    const targets = (data.contracts || []).filter((x) => !x.has_schedule && x.email);
    if (!window.confirm(`Envoyer le contrat + lien de paiement aux ${targets.length} élève(s) « à configurer » avec un courriel ?\n\nEnvoi RÉEL via Resend.`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/student-contracts/send-contract-bulk", { origin_url: window.location.origin, only_unconfigured: true });
      toast.success(`${r.data.sent} contrat(s) envoyé(s) · ${r.data.skipped_no_email} ignoré(s) (sans courriel).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi groupé impossible."); }
    finally { setBulkBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [dash, tar] = await Promise.all([
        api.get(`/member-payments/admin/contracts-dashboard${filter === "inactive" ? "?include_inactive=1" : ""}`),
        api.get("/dojo-tariffs").catch(() => ({ data: [] })),
      ]);
      setData(dash.data);
      setTariffs(Array.isArray(tar.data) ? tar.data : (tar.data?.tariffs || []));
    } catch (e) {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter]);

  const rows = useMemo(() => {
    let r = data.contracts || [];
    if (filter === "overdue") r = r.filter((x) => x.summary?.status === "en_retard");
    else if (filter === "unconfigured") r = r.filter((x) => !x.has_schedule);
    else if (filter === "active") r = r.filter((x) => ["actif", "active"].includes(x.statut));
    if (q.trim()) {
      const t = q.toLowerCase();
      r = r.filter((x) => (x.member_name || "").toLowerCase().includes(t) || (x.discipline || "").includes(t));
    }
    return r;
  }, [data, filter, q]);

  const k = data.kpis || {};

  return (
    <div className="space-y-6" data-testid="contrats-paiements-module">
      <div>
        <div className="text-red-400 text-[10px] tracking-[0.4em] mb-1">🧾 GESTION CENTRALE</div>
        <h1 className="text-2xl font-bold">Contrats &amp; Paiements</h1>
        <p className="text-zinc-500 text-sm mt-1">Tes contrats élèves, leurs échéanciers de versements et leur statut de paiement — au même endroit.</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi testid="kpi-active" label="Contrats actifs" value={k.active ?? "—"} />
        <Kpi testid="kpi-overdue" label="En retard" value={k.overdue ?? "—"} accent={k.overdue ? "text-red-400" : "text-white"} />
        <Kpi testid="kpi-due-week" label="Dû cette semaine" value={fmt(k.due_this_week)} accent="text-amber-300" />
        <Kpi testid="kpi-ending" label="Finissent <30j" value={k.ending_30d ?? "—"} />
        <Kpi testid="kpi-balance" label="Solde total à percevoir" value={fmt(k.total_balance)} accent="text-emerald-300" />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-2">
        <Input data-testid="search-input" placeholder="Rechercher un membre…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-zinc-950 border-zinc-800" />
        {[
          { id: "all", label: "Tous" },
          { id: "overdue", label: "🔴 En retard" },
          { id: "unconfigured", label: "À configurer" },
          { id: "active", label: "Actifs" },
          { id: "inactive", label: "💤 Inactifs / Résiliés" },
        ].map((f) => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${filter === f.id ? "bg-red-500/20 border-red-500/50 text-red-200" : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
            {f.label}
          </button>
        ))}
        <Button onClick={load} variant="outline" className="ml-auto border-zinc-700 text-zinc-300 text-xs h-8" data-testid="refresh-btn">↻ Actualiser</Button>
        <Button onClick={bulkSendUnconfigured} disabled={bulkBusy} className="bg-blue-600 hover:bg-blue-500 text-xs h-8" data-testid="bulk-send-contract-btn"><FileText size={13} className="mr-1"/> {bulkBusy ? "Envoi…" : "Envoyer contrat aux « à configurer »"}</Button>
        <Button onClick={() => navigate("/app/activation-membres")} variant="outline" className="border-emerald-700 bg-emerald-900/30 text-emerald-200 text-xs h-8" data-testid="activation-shortcut-btn"><ExternalLink size={13} className="mr-1"/> Activation membres</Button>
      </div>

      <ClientDuplicatesPanel onChanged={load} />

      {checked.size > 0 && (
        <div className="sticky top-2 z-20 flex flex-wrap items-center gap-2 rounded-xl border border-amber-500/40 bg-zinc-900/95 px-3 py-2 text-xs" data-testid="merge-bar">
          <span className="text-amber-200 font-medium">{nChecked} fiche(s) cochée(s)</span>
          <Button size="sm" onClick={() => mergeContracts(checkedRows, { onDone: afterMerge })} disabled={nChecked < 2} className="h-7 text-xs bg-red-600 hover:bg-red-500" data-testid="merge-checked-btn"><GitMerge size={13} className="mr-1" /> Fusionner (doublons même discipline)</Button>
          <Button size="sm" onClick={() => unifyClient(checkedRows, { onDone: afterMerge })} disabled={nChecked < 2} variant="outline" className="h-7 text-xs border-amber-500/50 text-amber-200" data-testid="unify-checked-btn"><Link2 size={13} className="mr-1" /> Unifier le client (plusieurs disciplines)</Button>
          <button onClick={() => setChecked(new Set())} className="ml-auto text-zinc-400 hover:text-zinc-200" data-testid="merge-clear-btn">Tout décocher</button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 text-zinc-400 text-xs">
            <tr>
              <th className="px-2 py-2" title="Cocher pour fusionner / unifier"></th>
              <th className="text-left px-3 py-2">Membre</th>
              <th className="text-left px-3 py-2">Discipline</th>
              <th className="text-left px-3 py-2">Mode</th>
              <th className="text-left px-3 py-2">Versements</th>
              <th className="text-right px-3 py-2">Solde</th>
              <th className="text-left px-3 py-2">Prochaine échéance</th>
              <th className="text-left px-3 py-2">Statut</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-zinc-500">Chargement…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-zinc-500">Aucun contrat.</td></tr>
            ) : rows.map((c) => (
              <tr key={c.contract_id} className="border-t border-zinc-900 hover:bg-zinc-900/40" data-testid={`row-${c.contract_id}`}>
                <td className="px-2 py-2"><input type="checkbox" checked={checked.has(c.contract_id)} onChange={() => toggleChecked(c.contract_id)} data-testid={`check-${c.contract_id}`} /></td>
                <td className="px-3 py-2 font-medium">{c.member_name}</td>
                <td className="px-3 py-2 text-zinc-400">{DISCIPLINE_LABELS[c.discipline] || c.discipline || "—"}</td>
                <td className="px-3 py-2 text-zinc-400">{c.payment_mode === "installments" ? "Versements" : c.payment_mode === "full" ? "Complet" : "—"}</td>
                <td className="px-3 py-2 text-zinc-400">{c.summary ? `${c.summary.installments_paid}/${c.summary.installments_total}` : "—"}</td>
                <td className="px-3 py-2 text-right">{c.summary ? fmt(c.summary.balance) : "—"}</td>
                <td className="px-3 py-2 text-zinc-400">{c.summary?.next_due ? `${c.summary.next_due.due_date} · ${fmt(c.summary.next_due.amount)}` : "—"}</td>
                <td className="px-3 py-2"><StatusBadge summary={c.summary} hasSchedule={c.has_schedule} /></td>
                <td className="px-3 py-2 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" onClick={() => setSelected(c)} className="h-7 text-xs bg-red-600 hover:bg-red-500" data-testid={`open-${c.contract_id}`}>
                      {c.has_schedule ? "Gérer" : "Configurer"}
                    </Button>
                    <button onClick={() => sendContractPayment(c)} data-testid={`send-contract-${c.contract_id}`} title="Envoyer contrat (PDF) + lien de paiement" className="p-1.5 rounded hover:bg-blue-500/15 text-zinc-400 hover:text-blue-300"><FileText size={15}/></button>
                    <button onClick={() => confirmActivated(c)} data-testid={`confirm-activated-${c.contract_id}`} title="Confirmer activé" className="p-1.5 rounded hover:bg-emerald-500/15 text-zinc-400 hover:text-emerald-300"><UserCheck size={15}/></button>
                    <button onClick={() => resilier(c)} data-testid={`resilier-${c.contract_id}`} title="Résilier le contrat" className="p-1.5 rounded hover:bg-amber-500/15 text-zinc-400 hover:text-amber-300"><Ban size={15}/></button>
                    <button onClick={() => supprimer(c)} data-testid={`supprimer-${c.contract_id}`} title="Supprimer l'élève" className="p-1.5 rounded hover:bg-red-500/15 text-zinc-400 hover:text-red-400"><Trash2 size={15}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <ContractDialog contract={selected} tariffs={tariffs} onClose={() => setSelected(null)} onChanged={load} />
      )}
    </div>
  );
}

function ContractDialog({ contract, tariffs, onClose, onChanged }) {
  const [sched, setSched] = useState(null);
  const [busy, setBusy] = useState(false);
  const [card, setCard] = useState(null);
  const memberEmail = contract.email || "";

  const loadCard = async () => {
    if (!memberEmail) { setCard({ has_card: false, no_email: true }); return; }
    try {
      const { data } = await api.get(`/member-payments/members/${encodeURIComponent(memberEmail)}/card`);
      setCard(data);
    } catch { setCard({ has_card: false }); }
  };
  useEffect(() => { loadCard(); }, []);

  const setupCard = async () => {
    try {
      const { data } = await api.post(
        `/member-payments/members/${encodeURIComponent(memberEmail)}/admin-setup-session`,
        { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Impossible d'ouvrir l'enregistrement de carte"); }
  };

  const toggleAutoPay = async () => {
    try {
      const next = !card?.auto_pay;
      await api.post(`/member-payments/members/${encodeURIComponent(memberEmail)}/auto-pay`, { enabled: next });
      toast.success(next ? "✅ Prélèvement automatique activé" : "Prélèvement automatique désactivé");
      await loadCard();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
  };
  // Form configuration
  const [tariffId, setTariffId] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [durationWeeks, setDurationWeeks] = useState("12");
  const [paymentMode, setPaymentMode] = useState("full");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState(null);

  const loadSchedule = async () => {
    try {
      const { data } = await api.get(`/member-payments/contracts/${contract.contract_id}/schedule`);
      setSched(data);
    } catch { setSched({ schedule: [], summary: null }); }
  };
  useEffect(() => { loadSchedule(); }, []);

  const dw = parseInt(durationWeeks || "0", 10);
  const installmentsAllowed = dw >= 12;

  const applyTariff = (id) => {
    setTariffId(id);
    const t = tariffs.find((x) => x.id === id);
    if (t) {
      setTotalPrice(String(t.price ?? ""));
      if (t.duration_weeks) setDurationWeeks(String(t.duration_weeks));
    }
  };

  const doPreview = async () => {
    if (!totalPrice || !dw) { toast.error("Renseigne le prix et la durée"); return; }
    try {
      const { data } = await api.post("/member-payments/schedule/preview", {
        total_price: parseFloat(totalPrice), duration_weeks: dw,
        payment_mode: paymentMode, start_date: startDate,
      });
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Aperçu impossible"); }
  };

  const generate = async () => {
    if (!totalPrice || !dw) { toast.error("Renseigne le prix et la durée"); return; }
    setBusy(true);
    try {
      await api.post(`/member-payments/contracts/${contract.contract_id}/generate-schedule`, {
        total_price: parseFloat(totalPrice), duration_weeks: dw,
        payment_mode: paymentMode, start_date: startDate,
      });
      toast.success("✅ Échéancier généré");
      setPreview(null);
      await loadSchedule();
      await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Génération impossible"); }
    finally { setBusy(false); }
  };

  const markPaid = async (pid) => {
    try {
      await api.post(`/member-payments/payments/${pid}/record-cash`, { note: "Encaissé (admin)" });
      toast.success("💵 Versement marqué payé (comptant)");
      await loadSchedule(); await onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const chargeCard = async (pid) => {
    try {
      const { data } = await api.post(`/member-payments/payments/${pid}/charge-saved-card`);
      if (data.ok) { toast.success(`💳 Carte chargée · reçu ${data.receipt_no}`); await loadSchedule(); await onChanged(); }
      else toast.error(data.error || "Carte refusée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur Stripe"); }
  };

  const remind = async (pid) => {
    try {
      await api.post(`/member-payments/payments/${pid}/send-reminder`);
      toast.success("📣 Relance envoyée (SMS + courriel)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Relance impossible"); }
  };

  const sendLink = async (pid) => {
    try {
      const { data } = await api.post(`/member-payments/payments/${pid}/send-payment-link`);
      if (data.ok) {
        const ch = [data.sms && "SMS", data.email && "courriel"].filter(Boolean).join(" + ");
        toast.success(`🔗 Lien de paiement envoyé par ${ch || "lien généré"}`);
      } else {
        toast.error(data.sms_error || data.email_error || "Aucun canal disponible (téléphone/courriel manquant)");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
  };

  const summary = sched?.summary;
  const isInactive = !["actif", "active"].includes(contract.statut);

  const reactivate = async () => {
    if (!window.confirm(`Réactiver le contrat de ${contract.member_name} ?`)) return;
    setBusy(true);
    try {
      await api.post(`/member-payments/admin/contracts/${contract.contract_id}/reactivate`);
      toast.success(`✅ Contrat de ${contract.member_name} réactivé`);
      await onChanged();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réactivation impossible");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="contract-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{contract.member_name}
            <span className="text-xs font-normal text-zinc-500">· {DISCIPLINE_LABELS[contract.discipline] || contract.discipline}</span>
          </DialogTitle>
          <DialogDescription>Échéancier de versements, paiements et relances.</DialogDescription>
        </DialogHeader>

        {isInactive && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-600/40 bg-amber-500/10 p-3" data-testid="reactivate-block">
            <div className="text-xs text-amber-200">Ce contrat est <b>inactif / résilié</b> (statut : {contract.statut || "inconnu"}).</div>
            <Button size="sm" onClick={reactivate} disabled={busy} data-testid="reactivate-contract-btn"
              className="h-8 text-xs bg-emerald-600 hover:bg-emerald-500 shrink-0">✅ Réactiver le contrat</Button>
          </div>
        )}

        {/* Résumé */}
        {summary && (
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="rounded-lg bg-zinc-900 p-2"><div className="text-[10px] text-zinc-500">Total</div><div className="font-bold">{fmt(summary.total_price)}</div></div>
            <div className="rounded-lg bg-zinc-900 p-2"><div className="text-[10px] text-zinc-500">Payé</div><div className="font-bold text-emerald-300">{fmt(summary.amount_paid)}</div></div>
            <div className="rounded-lg bg-zinc-900 p-2"><div className="text-[10px] text-zinc-500">Solde</div><div className="font-bold text-amber-300">{fmt(summary.balance)}</div></div>
            <div className="rounded-lg bg-zinc-900 p-2"><div className="text-[10px] text-zinc-500">Versements</div><div className="font-bold">{summary.installments_paid}/{summary.installments_total}</div></div>
          </div>
        )}

        {/* Carte enregistrée & prélèvement automatique */}
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 space-y-2" data-testid="card-autopay-block">
          <div className="text-xs font-semibold text-zinc-400">💳 Carte & prélèvement automatique</div>
          {!memberEmail ? (
            <div className="text-xs text-amber-400">Ce contrat n'a pas de courriel — ajoute un courriel à l'élève pour enregistrer une carte.</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm">
                  {card?.has_card ? (
                    <span className="text-emerald-400" data-testid="card-status">
                      {String(card.brand || "Carte").toUpperCase()} •••• {card.last4} (exp {String(card.exp_month).padStart(2, "0")}/{card.exp_year})
                    </span>
                  ) : (
                    <span className="text-zinc-500" data-testid="card-status">Aucune carte enregistrée</span>
                  )}
                </div>
                <Button size="sm" onClick={setupCard} className="h-7 text-[11px] bg-indigo-600 hover:bg-indigo-500" data-testid="setup-card-btn">
                  {card?.has_card ? "Changer la carte" : "💳 Enregistrer la carte au dojo"}
                </Button>
              </div>
              <div className="flex items-center justify-between gap-2 border-t border-zinc-800 pt-2">
                <div className="text-sm text-zinc-300">
                  Prélèvement automatique des versements
                  {card?.auto_pay
                    ? <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">ACTIVÉ</span>
                    : <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-zinc-700 text-zinc-300">désactivé</span>}
                </div>
                <Button
                  size="sm"
                  onClick={toggleAutoPay}
                  disabled={!card?.has_card}
                  title={!card?.has_card ? "Enregistre d'abord une carte" : ""}
                  className={`h-7 text-[11px] ${card?.auto_pay ? "bg-zinc-700 hover:bg-zinc-600" : "bg-emerald-600 hover:bg-emerald-500"}`}
                  data-testid="toggle-autopay-btn">
                  {card?.auto_pay ? "Désactiver" : "Activer l'auto-paiement"}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Échéancier */}
        <div>
          <div className="text-xs font-semibold text-zinc-400 mb-2">Échéancier</div>
          {sched?.schedule?.length ? (
            <div className="space-y-1.5" data-testid="schedule-list">
              {sched.schedule.map((p) => (
                <div key={p.id} className="flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2 text-sm">
                  <span className="text-zinc-500 w-6">#{p.installment_index}</span>
                  <span className="w-24 text-zinc-300">{p.due_date}</span>
                  <span className="font-medium">{fmt(p.amount)}</span>
                  {p.status === "paid" ? (
                    <span className="ml-auto text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">Payé ✓ {p.receipt_no || ""}</span>
                  ) : (
                    <div className="ml-auto flex gap-1.5">
                      <Button size="sm" onClick={() => markPaid(p.id)} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-500" data-testid={`paid-${p.id}`}>💵 Comptant</Button>
                      <Button size="sm" onClick={() => chargeCard(p.id)} className="h-7 text-[11px] bg-blue-600 hover:bg-blue-500" data-testid={`card-${p.id}`}>💳 Carte</Button>
                      <Button size="sm" onClick={() => sendLink(p.id)} className="h-7 text-[11px] bg-violet-600 hover:bg-violet-500" data-testid={`link-${p.id}`}>🔗 Lien</Button>
                      <Button size="sm" onClick={() => remind(p.id)} variant="outline" className="h-7 text-[11px] border-zinc-700" data-testid={`remind-${p.id}`}>📣</Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-lg p-4 text-center">Aucun échéancier — configure-le ci-dessous.</div>
          )}
        </div>

        {/* Assistant de configuration */}
        <div className="border-t border-zinc-800 pt-4 space-y-3">
          <div className="text-xs font-semibold text-red-400">⚙️ Configurer / régénérer l'échéancier</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-500">Plan tarifaire (optionnel)</label>
              <select value={tariffId} onChange={(e) => applyTariff(e.target.value)} data-testid="tariff-select"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-sm">
                <option value="">— Saisie manuelle —</option>
                {tariffs.filter((t) => t.active !== false).map((t) => (
                  <option key={t.id} value={t.id}>{t.label} · {fmt(t.price)}{t.duration_weeks ? ` · ${t.duration_weeks} sem` : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-500">Prix total (CAD)</label>
              <Input data-testid="total-price-input" type="number" value={totalPrice} onChange={(e) => setTotalPrice(e.target.value)} className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-500">Durée (semaines)</label>
              <select value={durationWeeks} onChange={(e) => { setDurationWeeks(e.target.value); if (parseInt(e.target.value) < 12) setPaymentMode("full"); }} data-testid="duration-select"
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg p-2 text-sm">
                {[4, 8, 12, 16, 24, 52].map((d) => <option key={d} value={d}>{d} semaines</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-500">Date du 1er versement</label>
              <Input data-testid="start-date-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="bg-zinc-900 border-zinc-800" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-zinc-500">Mode de paiement</label>
            <div className="flex gap-2 mt-1">
              <button onClick={() => setPaymentMode("full")} data-testid="mode-full"
                className={`text-xs px-3 py-1.5 rounded-full border ${paymentMode === "full" ? "bg-red-500/20 border-red-500/50 text-red-200" : "bg-zinc-900 border-zinc-800 text-zinc-400"}`}>Paiement complet</button>
              <button onClick={() => installmentsAllowed && setPaymentMode("installments")} data-testid="mode-installments" disabled={!installmentsAllowed}
                className={`text-xs px-3 py-1.5 rounded-full border ${paymentMode === "installments" ? "bg-red-500/20 border-red-500/50 text-red-200" : "bg-zinc-900 border-zinc-800 text-zinc-400"} ${!installmentsAllowed ? "opacity-40 cursor-not-allowed" : ""}`}>
                Versements aux 4 sem {installmentsAllowed ? `(${Math.floor(dw / 4)}×)` : "(12 sem+)"}</button>
            </div>
            {!installmentsAllowed && <p className="text-[10px] text-zinc-600 mt-1">⚠️ Les versements ne sont permis qu'à partir de 12 semaines.</p>}
          </div>

          {preview && (
            <div className="rounded-lg bg-zinc-900 p-3 text-xs" data-testid="preview-box">
              <div className="text-zinc-400 mb-1">Aperçu : {preview.num_installments} versement(s)</div>
              {preview.schedule.map((s) => <div key={s.index} className="flex justify-between text-zinc-300"><span>#{s.index} · {s.due_date}</span><span>{fmt(s.amount)}</span></div>)}
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={doPreview} variant="outline" className="border-zinc-700 text-zinc-300" data-testid="preview-btn">👁️ Aperçu</Button>
            <Button onClick={generate} disabled={busy} className="bg-red-600 hover:bg-red-500" data-testid="generate-schedule-btn">
              {busy ? "…" : "✅ Générer l'échéancier"}
            </Button>
          </div>
          <p className="text-[10px] text-zinc-600">Régénérer conserve les versements déjà payés et recrée seulement ceux à venir.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
